package com.karyadina.ui.screens

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Note
import androidx.compose.material.icons.automirrored.filled.Notes
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.karyadina.model.ChecklistItem
import com.karyadina.model.NoteType
import com.karyadina.model.QuickNote

import com.karyadina.service.QuickNotesServiceImpl
import com.karyadina.service.OCRService
import com.karyadina.service.PlatformFileHelper
import com.karyadina.util.currentTimeMillis
import com.karyadina.util.rememberDocumentScannerLauncher
import com.karyadina.util.rememberImagePickerLauncher
import com.karyadina.util.rememberImageBitmapFromUri
import kotlinx.coroutines.launch
import org.koin.compose.koinInject
import com.karyadina.ui.components.StudyFloatingActionButton
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.util.rememberCameraPermissionLauncher
import org.jetbrains.compose.resources.stringResource
import com.karyadina.util.getLocalActivity


@Composable
fun ImageViewerDialog(
    uri: String,
    onDismiss: () -> Unit
) {
    Dialog(onDismissRequest = onDismiss) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(androidx.compose.ui.graphics.Color.Black)
                .clickable { onDismiss() },
            contentAlignment = Alignment.Center
        ) {
            val bitmap = rememberImageBitmapFromUri(uri)
            if (bitmap != null) {
                Image(
                    bitmap = bitmap,
                    contentDescription = stringResource(StudyStrings.NotesImgFullscreenDesc),
                    contentScale = androidx.compose.ui.layout.ContentScale.Fit,
                    modifier = Modifier.fillMaxSize()
                )
            } else {
                CircularProgressIndicator(color = androidx.compose.ui.graphics.Color.White)
            }
        }
    }
}

// Helper to format file size if needed, but we don't need it right now.

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun QuickNotesScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val scope = rememberCoroutineScope()

    val service = remember { QuickNotesServiceImpl() }
    var notes by remember { mutableStateOf(service.getAllNotes()) }
    var selectionMode by remember { mutableStateOf(false) }
    var selectedNotes by remember { mutableStateOf(setOf<String>()) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedFilter by remember { mutableStateOf<String?>(null) } // null = All, "Pinned", "Archived"
    
    var stats by remember { mutableStateOf(service.getNotesStats()) }
    var showAddNote by remember { mutableStateOf(false) }

    val snackbarHostState = remember { SnackbarHostState() }

    val filters = listOf(
        null to stringResource(StudyStrings.NotesFilterAll),
        "Pinned" to stringResource(StudyStrings.NotesFilterPinned),
        "Archived" to stringResource(StudyStrings.NotesFilterArchived)
    )
    
    fun refresh() {
        notes = when {
            searchQuery.isNotBlank() -> service.searchNotes(searchQuery)
            selectedFilter == "Pinned" -> service.getPinnedNotes()
            selectedFilter == "Archived" -> service.getArchivedNotes()
            else -> service.getAllNotes()
        }
        stats = service.getNotesStats()
    }

    // Observe storage refresh signals (e.g. from cloud sync)
    LaunchedEffect(Unit) {
        com.karyadina.service.AppStorage.refreshNotifier.collect {
            service.refreshFromStorage()
            refresh()
        }
    }
    
    val localeManager: com.karyadina.ui.AppLocaleManager = koinInject()
    val appStrings by localeManager.strings.collectAsState()
    
    CompositionLocalProvider(com.karyadina.ui.LocalAppStrings provides appStrings) {
    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text(com.karyadina.ui.LocalAppStrings.current.appTitle) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, stringResource(StudyStrings.Back))
                    }
                }
            )
        },
        floatingActionButton = {
            StudyFloatingActionButton(
                onClick = { showAddNote = true },
                icon = Icons.Filled.Add,
                contentDescription = stringResource(StudyStrings.NotesNewNote)
            )
        },
        floatingActionButtonPosition = FabPosition.End
    ) { padding ->
        val strings = com.karyadina.ui.LocalAppStrings.current
        Column(
            modifier = modifier
                .fillMaxSize()
                .padding(padding)
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.surface,
                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                        )
                    )
                )
        ) {
            // Enhanced Search Section with gradient background
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.surface,
                tonalElevation = 1.dp
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(
                            Brush.verticalGradient(
                                colors = listOf(
                                    MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f),
                                    MaterialTheme.colorScheme.surface
                                )
                            )
                        )
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Enhanced Search Bar
                    SearchBar(
                        query = searchQuery,
                        onQueryChange = { searchQuery = it },
                        onSearch = {},
                        active = false,
                        onActiveChange = {},
                        placeholder = { Text(stringResource(StudyStrings.NotesSearch)) },
                        leadingIcon = { Icon(Icons.Default.Search, null) },
                        trailingIcon = {
                            if (searchQuery.isNotEmpty()) {
                                IconButton(onClick = { searchQuery = "" }) {
                                    Icon(Icons.Default.Clear, stringResource(StudyStrings.ActionClose))
                                }
                            }
                        },
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
                    ) {}

                    // Enhanced Stats Row
                    NotesStatsRow(stats)

                    // Enhanced Filter Chips with icons
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        FilterChip(
                            selected = selectedFilter == "All",
                            onClick = {
                                selectedFilter = "All"
                                refresh()
                            },
                            label = { Text(stringResource(StudyStrings.NotesFilterAll)) },
                            leadingIcon = if (selectedFilter == "All") {
                                {
                                    Icon(
                                        Icons.AutoMirrored.Filled.Notes,
                                        null,
                                        Modifier.size(16.dp)
                                    )
                                }
                            } else null,
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                                selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer
                            )
                        )
                        FilterChip(
                            selected = selectedFilter == "Pinned",
                            onClick = {
                                selectedFilter = "Pinned"
                                refresh()
                            },
                            label = { Text(stringResource(StudyStrings.NotesFilterPinned)) },
                            leadingIcon = if (selectedFilter == "Pinned") {
                                { Icon(Icons.Filled.PushPin, null, Modifier.size(16.dp)) }
                            } else null,
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = MaterialTheme.colorScheme.secondaryContainer,
                                selectedLabelColor = MaterialTheme.colorScheme.onSecondaryContainer
                            )
                        )
                        FilterChip(
                            selected = selectedFilter == "Archived",
                            onClick = {
                                selectedFilter = "Archived"
                                refresh()
                            },
                            label = { Text(stringResource(StudyStrings.NotesFilterArchived)) },
                            leadingIcon = if (selectedFilter == "Archived") {
                                { Icon(Icons.Filled.Archive, null, Modifier.size(16.dp)) }
                            } else null,
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = MaterialTheme.colorScheme.tertiaryContainer,
                                selectedLabelColor = MaterialTheme.colorScheme.onTertiaryContainer
                            )
                        )
                    }
                }
            }

            Spacer(Modifier.height(16.dp))

            // Notes grid
            if (notes.isEmpty()) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(
                            Icons.AutoMirrored.Filled.Note,
                            null,
                            modifier = Modifier.size(64.dp),
                            tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.2f)
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        Text(
                            stringResource(StudyStrings.NotesMsgNoNotes),
                            style = MaterialTheme.typography.titleMedium,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.4f)
                        )
                        Text(
                            stringResource(StudyStrings.NotesMsgCreateFirst),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f)
                        )
                    }
                }
            } else {
                val msgDeleted = stringResource(StudyStrings.NotesMsgDeleted)
                val actionUndo = stringResource(StudyStrings.NotesActionUndo)
                
                NotesGrid(
                    notes = notes,
                    service = service,
                    onRefresh = { refresh() },
                    onScrollStateChanged = { scrollingDown ->
                        // isFabVisible = !scrollingDown // Removed logic as FAB is always visible now or handled differently
                    },
                    onDelete = { noteToDelete ->
                        // Perform delete
                        service.deleteNote(noteToDelete.id)
                        refresh()

                        // Show Undo Snackbar
                        scope.launch {
                            val result = snackbarHostState.showSnackbar(
                                message = msgDeleted,
                                actionLabel = actionUndo,
                                duration = SnackbarDuration.Short
                            )

                            if (result == SnackbarResult.ActionPerformed) {
                                // Restore note
                                service.restoreNote(noteToDelete)
                                refresh()
                            }
                        }
                    }
                )
            }
        }

        if (showAddNote) {
            AddNoteDialog(
                onDismiss = { showAddNote = false },
                onAdd = {
                    service.createNote(it)
                    refresh()
                    showAddNote = false
                }
            )
        }


    }
    } // End CompositionLocalProvider
}

@Composable
fun NotesGrid(
    notes: List<QuickNote>,
    service: QuickNotesServiceImpl,
    onRefresh: () -> Unit,
    onScrollStateChanged: (Boolean) -> Unit = {},
    onDelete: (QuickNote) -> Unit
) {
    val gridState = androidx.compose.foundation.lazy.staggeredgrid.rememberLazyStaggeredGridState()
    
    // Detect scroll direction
    LaunchedEffect(gridState.isScrollInProgress) {
        if (gridState.isScrollInProgress) {
            val firstVisibleItemIndex = gridState.firstVisibleItemIndex
            val firstVisibleItemScrollOffset = gridState.firstVisibleItemScrollOffset
            
            snapshotFlow { 
                Pair(gridState.firstVisibleItemIndex, gridState.firstVisibleItemScrollOffset)
            }.collect { (index, offset) ->
                val isScrollingDown = index > firstVisibleItemIndex || 
                    (index == firstVisibleItemIndex && offset > firstVisibleItemScrollOffset)
                onScrollStateChanged(isScrollingDown)
            }
        }
    }
    
    com.karyadina.ui.components.ResponsiveContent {
        LazyVerticalStaggeredGrid(
            state = gridState,
            columns = StaggeredGridCells.Adaptive(minSize = if (com.karyadina.util.isWeb) 280.dp else 155.dp),
            contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 16.dp, bottom = 80.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalItemSpacing = 12.dp
        ) {
            items(notes, key = { it.id }) { note ->
                NoteCard(
                    note, 
                    service, 
                    onRefresh = onRefresh,
                    onDelete = onDelete
                )
            }
        }
    }
}

@Composable
fun NotesStatsRow(stats: com.karyadina.service.NotesStats) {
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        NoteStatCard(stringResource(StudyStrings.StatsTotal), "${stats.totalNotes}", Icons.AutoMirrored.Filled.Notes, Modifier.weight(1f))
        NoteStatCard(stringResource(StudyStrings.NotesFilterPinned), "${stats.pinnedCount}", Icons.Filled.PushPin, Modifier.weight(1f))
        NoteStatCard(
            stringResource(StudyStrings.NotesStatTasks), 
            "${stats.completedTasks}/${stats.totalTasks}",
            Icons.Filled.CheckCircle,
            Modifier.weight(1f)
        )
    }
}

@Composable
fun NoteStatCard(
    title: String,
    value: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    modifier: Modifier = Modifier
) {
    ElevatedCard(modifier = modifier) {
        Column(
            modifier = Modifier.padding(8.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Icon(icon, null, modifier = Modifier.size(16.dp), tint = MaterialTheme.colorScheme.primary)
            Spacer(Modifier.height(4.dp))
            Text(value, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Text(
                title,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurface.copy(0.6f)
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NoteCard(
    note: QuickNote,
    service: QuickNotesServiceImpl,
    onRefresh: () -> Unit,
    onDelete: (QuickNote) -> Unit
) {
    val backgroundColor = parseHexColor(note.color) ?: MaterialTheme.colorScheme.surfaceVariant
    var showFullNote by remember { mutableStateOf(false) }
    
    ElevatedCard(
        onClick = { showFullNote = true },
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(containerColor = backgroundColor),
        elevation = CardDefaults.elevatedCardElevation(
            defaultElevation = if (note.isPinned) 3.dp else 2.dp
        )
    ) {
        Column(Modifier.fillMaxWidth().padding(12.dp)) {
            // Header with type icon and actions
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                Surface(
                    shape = MaterialTheme.shapes.extraSmall,
                    color = MaterialTheme.colorScheme.primary.copy(0.2f)
                ) {
                    Icon(
                        getNoteTypeIcon(NoteType.valueOf(note.noteType)),
                        null,
                        modifier = Modifier.size(16.dp).padding(2.dp),
                        tint = MaterialTheme.colorScheme.primary
                    )
                }
                
                Row {
                    IconButton(
                        onClick = {
                            service.togglePin(note.id)
                            onRefresh()
                        },
                        modifier = Modifier.size(24.dp)
                    ) {
                        Icon(
                            if (note.isPinned) Icons.Filled.PushPin else Icons.Outlined.PushPin,
                            null,
                            modifier = Modifier.size(16.dp),
                            tint = if (note.isPinned) MaterialTheme.colorScheme.primary 
                                   else MaterialTheme.colorScheme.onSurface.copy(0.5f)
                        )
                    }
                    
                    IconButton(
                        onClick = {
                            onDelete(note)
                        },
                        modifier = Modifier.size(24.dp)
                    ) {
                        Icon(
                            Icons.Outlined.Delete,
                            null,
                            modifier = Modifier.size(16.dp),
                            tint = MaterialTheme.colorScheme.error
                        )
                    }
                }
            }
            
            Spacer(Modifier.height(8.dp))
            
            // Title
            if (note.title.isNotBlank()) {
                Text(
                    note.title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(Modifier.height(4.dp))
            }
            
            // Content based on type
            when (note.noteType) {
                NoteType.CHECKLIST.name -> {
                    // Checklist progress indicator
                    val completedCount = note.checklistItems.count { it.checked }
                    val totalCount = note.checklistItems.size
                    val progress = if (totalCount > 0) completedCount.toFloat() / totalCount else 0f
                    
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        // Progress bar
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    stringResource(StudyStrings.NotesProgressLabel),
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.primary
                                )
                                Text(
                                    stringResource(StudyStrings.NotesChkProgressFormat, completedCount, totalCount),
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            }
                            LinearProgressIndicator(
                                progress = { progress },
                                modifier = Modifier.fillMaxWidth().height(6.dp),
                                color = if (progress >= 1f) MaterialTheme.colorScheme.tertiary 
                                       else MaterialTheme.colorScheme.primary,
                                trackColor = MaterialTheme.colorScheme.surfaceVariant,
                            )
                        }
                        
                        Spacer(Modifier.height(4.dp))
                        
                        // Checklist items with better UX
                        note.checklistItems.take(5).forEach { item ->
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clip(MaterialTheme.shapes.small)
                                    .background(
                                        if (item.checked) 
                                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
                                        else 
                                            MaterialTheme.colorScheme.surface
                                    )
                                    .padding(vertical = 4.dp, horizontal = 4.dp)
                            ) {
                                Checkbox(
                                    checked = item.checked,
                                    onCheckedChange = {
                                        service.updateChecklistItem(note.id, item.id, it)
                                        onRefresh()
                                    },
                                    modifier = Modifier.size(20.dp)
                                )
                                Text(
                                    item.text,
                                    style = MaterialTheme.typography.bodyMedium,
                                    maxLines = 2,
                                    overflow = TextOverflow.Ellipsis,
                                    textDecoration = if (item.checked) TextDecoration.LineThrough else null,
                                    color = if (item.checked) 
                                        MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                    else 
                                        MaterialTheme.colorScheme.onSurface,
                                    modifier = Modifier.weight(1f)
                                )
                            }
                        }
                        
                        // View Full button for long checklists
                        if (note.checklistItems.size > 5) {
                            var showFullNote by remember { mutableStateOf(false) }
                            
                            FilledTonalButton(
                                onClick = { showFullNote = true },
                                modifier = Modifier.fillMaxWidth(),
                                colors = ButtonDefaults.filledTonalButtonColors(
                                    containerColor = MaterialTheme.colorScheme.primaryContainer
                                )
                            ) {
                                Icon(
                                    Icons.Filled.Visibility,
                                    contentDescription = null,
                                    modifier = Modifier.size(16.dp)
                                )
                                Spacer(Modifier.width(8.dp))
                                Text(
                                    stringResource(StudyStrings.NotesChkViewAllFormat, note.checklistItems.size),
                                    style = MaterialTheme.typography.labelLarge,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                            
                            if (showFullNote) {
                                NoteDetailDialog(
                                    note = note,
                                    service = service,
                                    onDismiss = { showFullNote = false },
                                    onRefresh = onRefresh
                                )
                            }
                        }
                        
                        // Completion badge
                        if (progress >= 1f) {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(4.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .clip(MaterialTheme.shapes.small)
                                    .background(MaterialTheme.colorScheme.tertiaryContainer)
                                    .padding(horizontal = 8.dp, vertical = 4.dp)
                            ) {
                                Icon(
                                    Icons.Filled.CheckCircle,
                                    contentDescription = null,
                                    modifier = Modifier.size(14.dp),
                                    tint = MaterialTheme.colorScheme.tertiary
                                )
                                Text(
                                    stringResource(StudyStrings.NotesChkAllCompleted),
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.onTertiaryContainer
                                )
                            }
                        }
                    }
                }
                else -> {
                    if (note.content.isNotBlank()) {
                        Text(
                            note.content,
                            style = MaterialTheme.typography.bodySmall,
                            maxLines = 6,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
            
            // Attached Images Thumbnails
            if (note.imageUris.isNotEmpty()) {
                Spacer(Modifier.height(8.dp))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    modifier = Modifier.fillMaxWidth().height(60.dp)
                ) {
                    note.imageUris.take(2).forEach { uri ->
                        Box(
                            modifier = Modifier
                                .weight(1f)
                                .fillMaxHeight()
                                .clip(MaterialTheme.shapes.small)
                                .background(MaterialTheme.colorScheme.surfaceVariant)
                        ) {
                            val bitmap = rememberImageBitmapFromUri(uri)
                            if (bitmap != null) {
                                Image(
                                    bitmap = bitmap,
                                    contentDescription = stringResource(StudyStrings.NotesLabelAttachments),
                                    contentScale = androidx.compose.ui.layout.ContentScale.Crop,
                                    modifier = Modifier.fillMaxSize()
                                )
                            }
                        }
                    }
                }
            }
            
            // Tags and Footer
            if (note.tags.isNotEmpty() || true) {
                Spacer(Modifier.height(8.dp))
                Row(
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    // Tags
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.weight(1f, fill = false)
                    ) {
                        note.tags.take(2).forEach { tag ->
                            Surface(
                                shape = MaterialTheme.shapes.extraSmall,
                                color = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.7f)
                            ) {
                                Text(
                                    "#$tag",
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = FontWeight.Medium
                                )
                            }
                        }
                    }
                    
                    // Timestamp
                    Text(
                        formatRelativeTime(note.updatedAt),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f)
                    )
                }
                
                // Tap to view hint
                Spacer(Modifier.height(6.dp))
                Row(
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(
                        Icons.Filled.TouchApp,
                        contentDescription = null,
                        modifier = Modifier.size(12.dp),
                        tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.6f)
                    )
                    Spacer(Modifier.width(4.dp))
                    Text(
                        stringResource(StudyStrings.NotesViewFull),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.primary.copy(alpha = 0.6f),
                        fontWeight = FontWeight.Medium
                    )
                }
            }
            
            // Archive button at bottom
            if (!note.isArchived) {
                Spacer(Modifier.height(4.dp))
                TextButton(
                    onClick = {
                        service.toggleArchive(note.id)
                        onRefresh()
                    },
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp)
                ) {
                    Icon(
                        Icons.Outlined.Archive,
                        null,
                        modifier = Modifier.size(14.dp)
                    )
                    Spacer(Modifier.width(4.dp))
                    Text(
                        stringResource(StudyStrings.NotesArchive),
                        style = MaterialTheme.typography.labelSmall
                    )
                }
            }
        }
    }
    
    // Show full note dialog when card is clicked
    if (showFullNote) {
        NoteDetailDialog(
            note = note,
            service = service,
            onDismiss = { showFullNote = false },
            onRefresh = onRefresh
        )
    }
}

// Format relative time (e.g., "2 hours ago")
fun formatRelativeTime(timestamp: Long): String {
    val now = currentTimeMillis()
    val diff = now - timestamp
    
    return when {
        diff < 60_000 -> "Just now"
        diff < 3600_000 -> "${diff / 60_000}m ago"
        diff < 86400_000 -> "${diff / 3600_000}h ago"
        diff < 604800_000 -> "${diff / 86400_000}d ago"
        else -> "${diff / 604800_000}w ago"
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NoteDetailDialog(
    note: QuickNote,
    service: QuickNotesServiceImpl,
    onDismiss: () -> Unit,
    onRefresh: () -> Unit
) {
    val backgroundColor = parseHexColor(note.color) ?: MaterialTheme.colorScheme.surfaceVariant
    var selectedImageUri by remember { mutableStateOf<String?>(null) }

    if (selectedImageUri != null) {
        ImageViewerDialog(
            uri = selectedImageUri!!,
            onDismiss = { selectedImageUri = null }
        )
    }

    Dialog(onDismissRequest = onDismiss) {
        ElevatedCard(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(0.85f),
            colors = CardDefaults.elevatedCardColors(containerColor = backgroundColor)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
            ) {
                // Header
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.7f),
                    tonalElevation = 2.dp
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.weight(1f)
                        ) {
                            Surface(
                                shape = MaterialTheme.shapes.small,
                                color = MaterialTheme.colorScheme.primary.copy(0.2f)
                            ) {
                                Icon(
                                    getNoteTypeIcon(NoteType.valueOf(note.noteType)),
                                    null,
                                    modifier = Modifier.size(32.dp).padding(6.dp),
                                    tint = MaterialTheme.colorScheme.primary
                                )
                            }
                            
                            Column(modifier = Modifier.weight(1f)) {
                                if (note.title.isNotBlank()) {
                                    Text(
                                        note.title,
                                        style = MaterialTheme.typography.titleLarge,
                                        fontWeight = FontWeight.Bold,
                                        maxLines = 2,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                } else {
                                    Text(
                                        stringResource(StudyStrings.NotesDetailsTitle),
                                        style = MaterialTheme.typography.titleLarge,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                                Text(
                                    formatRelativeTime(note.updatedAt),
                                    style = MaterialTheme.typography.labelMedium,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                )
                            }
                        }
                        
                        IconButton(onClick = onDismiss) {
                            Icon(Icons.Filled.Close, stringResource(StudyStrings.ActionClose))
                        }
                    }
                }
                
                // Content
                LazyColumn(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // For checklist type
                    if (note.noteType == NoteType.CHECKLIST.name) {
                        item {
                            // Progress indicator
                            val completedCount = note.checklistItems.count { it.checked }
                            val totalCount = note.checklistItems.size
                            val progress = if (totalCount > 0) completedCount.toFloat() / totalCount else 0f
                            
                            ElevatedCard(
                                elevation = CardDefaults.elevatedCardElevation(defaultElevation = 3.dp)
                            ) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .background(
                                            Brush.horizontalGradient(
                                                colors = listOf(
                                                    MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f),
                                                    MaterialTheme.colorScheme.tertiaryContainer.copy(alpha = 0.5f)
                                                )
                                            )
                                        )
                                        .padding(16.dp),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Text(
                                            stringResource(StudyStrings.NotesProgressLabel),
                                            style = MaterialTheme.typography.titleMedium,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.primary
                                        )
                                        Text(
                                            stringResource(StudyStrings.NotesChkProgressFormat, completedCount, totalCount),
                                            style = MaterialTheme.typography.titleSmall,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.primary
                                        )
                                    }
                                    
                                    LinearProgressIndicator(
                                        progress = { progress },
                                        modifier = Modifier.fillMaxWidth().height(8.dp),
                                        color = if (progress >= 1f) MaterialTheme.colorScheme.tertiary 
                                               else MaterialTheme.colorScheme.primary,
                                        trackColor = MaterialTheme.colorScheme.surfaceVariant,
                                    )
                                    
                                    if (progress >= 1f) {
                                        Row(
                                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Icon(
                                                Icons.Filled.CheckCircle,
                                                contentDescription = null,
                                                modifier = Modifier.size(18.dp),
                                                tint = MaterialTheme.colorScheme.tertiary
                                            )
                                            Text(
                                                stringResource(StudyStrings.NotesChkAllCompletedEmoji),
                                                style = MaterialTheme.typography.bodyMedium,
                                                fontWeight = FontWeight.Bold,
                                                color = MaterialTheme.colorScheme.tertiary
                                            )
                                        }
                                    } else {
                                        Text(
                                            stringResource(StudyStrings.NotesChkTasksRemainingFormat, totalCount - completedCount),
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                                        )
                                    }
                                }
                            }
                        }
                        
                        // All checklist items
                        items(note.checklistItems.size) { index ->
                            val item = note.checklistItems[index]
                            ElevatedCard(
                                elevation = CardDefaults.elevatedCardElevation(
                                    defaultElevation = if (item.checked) 1.dp else 2.dp
                                )
                            ) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .background(
                                            if (item.checked) 
                                                MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
                                            else 
                                                MaterialTheme.colorScheme.surface
                                        )
                                        .padding(12.dp)
                                ) {
                                    Checkbox(
                                        checked = item.checked,
                                        onCheckedChange = {
                                            service.updateChecklistItem(note.id, item.id, it)
                                            onRefresh()
                                        },
                                        modifier = Modifier.size(24.dp)
                                    )
                                    Text(
                                        item.text,
                                        style = MaterialTheme.typography.bodyLarge,
                                        textDecoration = if (item.checked) TextDecoration.LineThrough else null,
                                        color = if (item.checked) 
                                            MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                        else 
                                            MaterialTheme.colorScheme.onSurface,
                                        modifier = Modifier.weight(1f)
                                    )
                                }
                            }
                        }

                    } else {
                        // For text notes
                        item {
                            Text(
                                note.content,
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                        }
                    }
                    
                    // Display Attached Images
                    if (note.imageUris.isNotEmpty()) {
                        item {
                            Spacer(Modifier.height(8.dp))
                            }
                            item {
                                Spacer(Modifier.height(8.dp))
                                Text(
                                    stringResource(StudyStrings.NotesLabelAttachments),
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold
                                )
                            }

                        items(note.imageUris, key = { it }) { uri ->
                             ElevatedCard(
                                onClick = { selectedImageUri = uri },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .heightIn(min = 200.dp, max = 400.dp)
                            ) {
                                val bitmap = rememberImageBitmapFromUri(uri)
                                if (bitmap != null) {
                                    Image(
                                        bitmap = bitmap,
                                        contentDescription = stringResource(StudyStrings.NotesLabelAttachments),
                                        contentScale = androidx.compose.ui.layout.ContentScale.FillWidth,
                                        modifier = Modifier.fillMaxSize()
                                    )
                                } else {
                                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                        CircularProgressIndicator()
                                    }
                                }
                            }
                        }
                    }
                    
                    // Tags
                    if (note.tags.isNotEmpty()) {
                        item {
                            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text(
                                    stringResource(StudyStrings.NotesLabelTags),
                                    style = MaterialTheme.typography.labelLarge,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.primary
                                )
                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    note.tags.forEach { tag ->
                                        Surface(
                                            shape = MaterialTheme.shapes.small,
                                            color = MaterialTheme.colorScheme.secondaryContainer
                                        ) {
                                            Text(
                                                "#$tag",
                                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                                style = MaterialTheme.typography.bodyMedium,
                                                fontWeight = FontWeight.Medium
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AddNoteDialog(
    onDismiss: () -> Unit,
    onAdd: (QuickNote) -> Unit
) {
    var title by remember { mutableStateOf("") }
    var content by remember { mutableStateOf("") }
    var selectedType by remember { mutableStateOf(NoteType.TEXT) }
    var selectedColor by remember { mutableStateOf("#FFFFFF") }
    var attachedImages by remember { mutableStateOf(listOf<String>()) }
    var isExtractingText by remember { mutableStateOf(false) }
    var selectedImageUri by remember { mutableStateOf<String?>(null) }
    
    val ocrService = koinInject<OCRService>()
    val translationService = koinInject<com.karyadina.service.TranslationService>()
    val scope = rememberCoroutineScope()
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isTranslating by remember { mutableStateOf(false) }

    


    if (selectedImageUri != null) {
        ImageViewerDialog(
            uri = selectedImageUri!!,
            onDismiss = { selectedImageUri = null }
        )
    }
    
    val scannerLauncher = rememberDocumentScannerLauncher { uri ->
        if (uri != null) {
            isExtractingText = true
            scope.launch {
                val result = ocrService.extractText(uri)
                if (result.isSuccess) {
                    val extracted = result.getOrDefault("")
                    if (content.isBlank()) content = extracted
                    else content += "\n\n$extracted"
                }
                isExtractingText = false
            }
        }
    }
    
    
    val cameraPermissionLauncher = rememberCameraPermissionLauncher { isGranted ->
        if (isGranted) {
            scannerLauncher.launch()
        } else {
             // Show toast or snackbar or error
             // Since we have limited UI space in dialog, maybe set a temporary error or just ignore if denied repeatedly
             errorMessage = "Camera permission required"
        }
    }

    val imagePickerLauncher = rememberImagePickerLauncher { uris ->
        attachedImages = attachedImages + uris
    }

    
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(StudyStrings.NotesNewNote)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                // Note type selector
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    listOf(NoteType.TEXT, NoteType.CHECKLIST).forEach { type ->
                        FilterChip(
                            selected = selectedType == type,
                            onClick = { selectedType = type },
                            label = { Text(type.name) },
                            leadingIcon = {
                                Icon(
                                    getNoteTypeIcon(type),
                                    null,
                                    Modifier.size(16.dp)
                                )
                            }
                        )
                    }
                }
                OutlinedTextField(
                    value = title,
                    onValueChange = { title = it },
                    label = { Text(stringResource(StudyStrings.TaskTitleLabel)) },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                
                Box {
                    OutlinedTextField(
                        value = content,
                        onValueChange = { content = it },
                        label = { Text(if (selectedType == NoteType.CHECKLIST) "Items (one per line)" else "Content") },
                        modifier = Modifier.fillMaxWidth().height(120.dp),
                        maxLines = 5
                    )
                    if (isExtractingText) {
                        LinearProgressIndicator(
                            modifier = Modifier.fillMaxWidth().align(Alignment.BottomCenter)
                        )
                    }
                }
                
                // OCR and Image Buttons
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    if (!com.karyadina.util.isWeb) {
                        OutlinedButton(
                            onClick = { cameraPermissionLauncher.launch() },
                            modifier = Modifier.weight(1f),
                            contentPadding = PaddingValues(horizontal = 8.dp)
                        ) {
                            Icon(Icons.Filled.DocumentScanner, null, Modifier.size(16.dp))
                            Spacer(Modifier.width(4.dp))
                            Text(stringResource(StudyStrings.NotesActionScan), style = MaterialTheme.typography.labelMedium)
                        }
                    }
                    
                    OutlinedButton(
                        onClick = { imagePickerLauncher.launchPickImage(2) },
                        modifier = Modifier.weight(1f),
                        contentPadding = PaddingValues(horizontal = 8.dp)
                    ) {
                        Icon(Icons.Filled.Image, null, Modifier.size(16.dp))
                        Spacer(Modifier.width(4.dp))
                        Text(stringResource(StudyStrings.NotesActionImage), style = MaterialTheme.typography.labelMedium)
                    }
                }
                
                // Translation Button
                if (!com.karyadina.util.isWeb && content.isNotBlank()) {
                     OutlinedButton(
                        onClick = {
                            isTranslating = true
                            scope.launch {
                                // Defaulting to En -> Hi for now as requested
                                val result = translationService.translate(content, "en", "hi")
                                if (result.isSuccess) {
                                    content += "\n\n[Hindi]:\n${result.getOrNull()}"
                                } else {
                                    errorMessage = "Translation failed: ${result.exceptionOrNull()?.message}"
                                }
                                isTranslating = false
                            }
                        },
                        enabled = !isTranslating,
                        modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                        contentPadding = PaddingValues(horizontal = 8.dp)
                    ) {
                        if (isTranslating) {
                            CircularProgressIndicator(modifier = Modifier.size(16.dp), strokeWidth = 2.dp)
                            Spacer(Modifier.width(8.dp))
                            Text("Translating...", style = MaterialTheme.typography.labelMedium)
                        } else {
                            Icon(Icons.Filled.Translate, null, Modifier.size(16.dp))
                            Spacer(Modifier.width(4.dp))
                            Text(stringResource(StudyStrings.NotesActionTranslate), style = MaterialTheme.typography.labelMedium)
                        }
                    }
                }
                
                if (errorMessage != null) {
                    Text(
                        errorMessage!!,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.labelSmall,
                        modifier = Modifier.padding(top = 4.dp, start = 4.dp)
                    )
                }

                
                // Attached Images Preview
                if (attachedImages.isNotEmpty()) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.fillMaxWidth().height(60.dp)
                    ) {
                        attachedImages.forEachIndexed { index, uri ->
                             ElevatedCard(
                                onClick = { selectedImageUri = uri },
                                modifier = Modifier.size(60.dp)
                            ) {
                                Box(Modifier.fillMaxSize()) {
                                    val bitmap = rememberImageBitmapFromUri(uri)
                                    if (bitmap != null) {
                                        Image(
                                            bitmap = bitmap,
                                            contentDescription = null,
                                            contentScale = androidx.compose.ui.layout.ContentScale.Crop,
                                            modifier = Modifier.fillMaxSize()
                                        )
                                    }
                                    
                                    // Delete button
                                    IconButton(
                                        onClick = { 
                                            attachedImages = attachedImages.filterIndexed { i, _ -> i != index }
                                        },
                                        modifier = Modifier.align(Alignment.TopEnd).size(20.dp).background(MaterialTheme.colorScheme.background.copy(0.5f), androidx.compose.foundation.shape.CircleShape)
                                    ) {
                                        Icon(Icons.Filled.Close, null, Modifier.size(12.dp))
                                    }
                                }
                            }
                        }
                    }
                }
                
                // Color selector
                Text("Color:", style = MaterialTheme.typography.labelSmall)
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    listOf(
                        "#FFFFFF" to "White",
                        "#FFEBEE" to "Red",
                        "#FFF9C4" to "Yellow",
                        "#E8F5E9" to "Green",
                        "#E3F2FD" to "Blue"
                    ).forEach { (color, _) ->
                        Surface(
                            onClick = { selectedColor = color },
                            modifier = Modifier.size(32.dp),
                            shape = MaterialTheme.shapes.small,
                            color = parseHexColor(color) ?: MaterialTheme.colorScheme.surface,
                            border = if (selectedColor == color) 
                                androidx.compose.foundation.BorderStroke(2.dp, MaterialTheme.colorScheme.primary)
                            else null
                        ) {}
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    val checklistItems = if (selectedType == NoteType.CHECKLIST) {
                        content.lines()
                            .filter { it.isNotBlank() }
                            .mapIndexed { index, line ->
                                ChecklistItem(
                                    id = "ITEM_${currentTimeMillis()}_${index}_${(1000..9999).random()}",
                                    text = line.trim(),
                                    order = index
                                ) 
                            }
                    } else emptyList()
                    
                    scope.launch {
                        // Save images to internal storage
                        val savedUris = attachedImages.mapNotNull { uri ->
                            PlatformFileHelper.saveImageToAppStorage(uri)
                        }
                        
                        onAdd(QuickNote(
                            id = "",
                            title = title,
                            content = if (selectedType == NoteType.TEXT) content else "",
                            noteType = selectedType.name,
                            color = selectedColor,
                            checklistItems = checklistItems,
                            imageUris = savedUris,
                            createdAt = currentTimeMillis(),
                            updatedAt = currentTimeMillis()
                        ))
                    }
                }
            ) {
                Text(stringResource(StudyStrings.Addbtn))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(stringResource(StudyStrings.Cancel)) }
        }
    )
}

fun getNoteTypeIcon(type: NoteType): androidx.compose.ui.graphics.vector.ImageVector {
    return when (type) {
        NoteType.TEXT -> Icons.Filled.TextFields
        NoteType.CHECKLIST -> Icons.Filled.Checklist
        NoteType.VOICE_MEMO -> Icons.Filled.Mic
        NoteType.SKETCH -> Icons.Filled.Brush
        NoteType.CODE_SNIPPET -> Icons.Filled.Code
    }
}

fun parseHexColor(hex: String): androidx.compose.ui.graphics.Color? {
    return try {
        val cleanHex = hex.removePrefix("#")
        val argb = when (cleanHex.length) {
            6 -> "FF$cleanHex" // Add alpha if missing
            8 -> cleanHex
            else -> return null
        }
        val color = argb.toLongOrNull(16) ?: return null
        androidx.compose.ui.graphics.Color(color)
    } catch (e: Exception) {
        null
    }
}
